
/**
 * Welke kolommen het huishoudenoverzicht toont, per organisatie.
 *
 * Het overzicht is elf kolommen breed en niet elke organisatie leest ze
 * allemaal. Dit is puur weergave: er wordt geen veld verborgen dat elders
 * meetelt, en er verandert niets aan wat er opgeslagen staat.
 *
 * **Kolommen kiezen is niet alleen kolommen toevoegen.** Een organisatie die er
 * eentje bij wil, wil er meestal ook een paar weg — anders wordt de tabel
 * alleen maar breder. Vandaar dat dezelfde instelling allebei doet.
 *
 * AI-CONTEXT: See docs/features/tenant-status-and-column-config.md
 */

/**
 * @typedef {object} HouseholdColumn
 * @property {string} key - Sleutel zoals de tabel hem kent
 * @property {string} label - Kolomkop
 * @property {boolean} [fixed] - Niet uit te zetten
 * @property {boolean} [defaultHidden] - Staat uit tenzij de organisatie hem aanzet
 */

/**
 * De kolommen waar iets te kiezen valt, in de volgorde waarin ze staan.
 *
 * De besturingskolommen staan hier bewust NIET in: de sleepgreep, 'Aanpassen'
 * en 'Verwijderen' zijn knoppen en geen gegevens, en die weghalen zou een
 * handeling onbereikbaar maken in plaats van een kolom rustiger.
 *
 * @type {Array<HouseholdColumn>}
 */
const HOUSEHOLD_COLUMN_CATALOG = [
    // Vast: hieraan hangt de link naar het huishouden. Zonder deze kolom is een
    // rij niet meer te openen, dus dit is geen keuze maar de tabel zelf.
    { key: 'title', label: 'STRAAT', fixed: true },
    { key: 'name', label: 'NAAM' },
    { key: 'status', label: 'STATUS' },
    { key: 'labels', label: 'LABELS' },
    // Waarmee het huishouden geholpen wordt (fixer, energiecoach, …), uit de
    // lijst bij Instellingen → Ondersteuning → 'Diensten'. Standaard uit: de
    // meeste organisaties gebruiken geen diensten, en dan is het een lege kolom.
    // AI-CONTEXT: See docs/features/huishoudens-status-per-periode.md
    { key: 'diensten', label: 'DIENSTEN', defaultHidden: true },
    { key: 'registered', label: 'AANMELDDATUM' },
    { key: 'contact', label: 'LAATSTE CONTACT' },
    { key: 'visit', label: 'VOLGENDE AFSPRAAK' },
    { key: 'tasks', label: 'TAKEN' },
    { key: 'notes', label: 'NOTITIES' },
    { key: 'assigned', label: 'TOEGEWEZEN AAN' },
    // Staat naast 'Toegewezen aan' omdat de planner beide in één blik leest:
    // wie werkt eraan, en mag iemand het anders zelf oppakken.
    //
    // De enige kolom die standaard UIT staat. Hij hoort bij het vrijgeven van
    // huishoudens aan klussers op de telefoon, en dat doet lang niet iedereen —
    // een elfde kolom bij elke organisatie is dan ruis. Wie hem wil, zet hem aan
    // bij Instellingen → Ondersteuning.
    { key: 'selfPickup', label: 'ZELF OP TE PAKKEN', defaultHidden: true },
    // Staat er alleen bij een organisatie die met meer dan één locatie werkt;
    // dat gaat buiten deze instelling om (zie `Overview.jsx`), want bij nul of
    // één locatie valt er niets te kiezen en is de kolom ruis. Wie wél locaties
    // heeft maar de kolom niet wil, zet hem hier uit.
    { key: 'location', label: 'LOCATIE' },
]

/** @type {Array<string>} */
const CHOOSABLE_COLUMN_KEYS = HOUSEHOLD_COLUMN_CATALOG
    .filter((column) => !column.fixed)
    .map((column) => column.key)

/**
 * Gegevenstitels die een vaste kolom al toont.
 *
 * Kiest iemand er hier toch een als extra kolom, dan stond dezelfde waarde twee
 * keer in de rij — met twee verschillende koppen, want de vaste kolom heet
 * 'STRAAT' en het gegeven 'Straatnaam'.
 *
 * @type {Array<string>}
 */
const COLUMN_OWNED_TITLES = ['Straatnaam', 'Huisnummer', 'Naam']

/** @type {Record<string, HouseholdColumn>} */
const KOLOM_PER_SLEUTEL = HOUSEHOLD_COLUMN_CATALOG
    .reduce((acc, column) => ({ ...acc, [column.key]: column }), {})

/**
 * Of deze kolom verborgen is: de instelling van de organisatie, en anders de
 * standaard van de kolom zelf.
 *
 * **Geëxporteerd omdat het instellingenscherm precies dit antwoord nodig heeft.**
 * De schakelaar daar schrijft zijn stand rechtstreeks in
 * `HouseholdColumns.Hidden.<sleutel>`, dus de schakelaar ís het veld. Zou dat
 * scherm alleen naar de opgeslagen waarde kijken, dan staat hij bij een kolom
 * die standaard uit is op 'niet verbergen' terwijl de kolom ontbreekt — twee
 * waarheden voor dezelfde instelling, en de eerste klik zou dan het
 * tegenovergestelde opslaan van wat de gebruiker ziet.
 * AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
 *
 * Alleen een duidelijke ja of nee telt als keuze; al het andere (`undefined`,
 * een lege string, onzin) valt terug op de standaard van de kolom.
 * AI-CONTEXT: See docs/pitfalls.md#ui-togglebutton-string-only-branches
 *
 * @param {Record<string, boolean|string>} [hidden] - `HouseholdColumns.Hidden`
 * @param {string} key - Kolomsleutel
 * @returns {boolean}
 */
const columnHidden = (hidden, key) => {
    const gezet = hidden ? hidden[key] : undefined
    if (gezet === true || gezet === 'true') return true
    if (gezet === false || gezet === 'false') return false
    return Boolean(KOLOM_PER_SLEUTEL[key] && KOLOM_PER_SLEUTEL[key].defaultHidden)
}

/**
 * @typedef {object} HouseholdColumnsConfig
 * @property {Record<string, boolean|string>} [Hidden] - Per kolomsleutel of hij uit staat
 * @property {Array<string>} [Extra] - Titels van `PersonaData`-rijen als extra kolom
 * @property {boolean|string} [ContactDateOnly] - In 'LAATSTE CONTACT' alleen de datum, zonder uitkomst en reeks
 */

/**
 * @typedef {object} HouseholdColumnsResult
 * @property {Array<HouseholdColumn>} columns - Wat er getoond wordt, in vaste volgorde
 * @property {Array<string>} hiddenKeys - Wat er uit staat; voor het instellingenscherm
 * @property {Array<string>} extraTitles - Eigen gegevenstitels als extra kolom
 * @property {boolean} contactDateOnly - Of 'LAATSTE CONTACT' alleen de datum toont (verzoek BuurtKlusBedrijf 2026-09-28)
 */

/**
 * @param {HouseholdColumnsConfig} [config] - `CompagnyMeta.HouseholdColumns`
 * @returns {HouseholdColumnsResult}
 */
const resolveHouseholdColumns = (config) => {
    // Alles wat geen gewone map is — nog niet geladen, een mislukte read, een
    // array — leest als "geen instelling", en dan staat alles aan.
    // AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
    const settings = (config && typeof config === 'object' && !Array.isArray(config))
        ? config
        : {}

    const hidden = (settings.Hidden && typeof settings.Hidden === 'object' && !Array.isArray(settings.Hidden))
        ? settings.Hidden
        : {}

    const hiddenKeys = CHOOSABLE_COLUMN_KEYS.filter((key) => columnHidden(hidden, key))

    const columns = HOUSEHOLD_COLUMN_CATALOG
        .filter((column) => column.fixed || !hiddenKeys.includes(column.key))

    const gezien = new Set(COLUMN_OWNED_TITLES)
    const extraTitles = (Array.isArray(settings.Extra) ? settings.Extra : [])
        .filter((titel) => typeof titel === 'string')
        .map((titel) => titel.trim())
        .filter((titel) => {
            if (titel === '' || gezien.has(titel)) return false
            gezien.add(titel)
            return true
        })

    // ToggleButton schrijft tekst, andere schrijvers booleans; beide tellen. Standaard
    // UIT: de uitkomst en de reeks 'n× geen contact' zijn juist wat de meeste
    // organisaties in deze kolom zoeken.
    const contactDateOnly = settings.ContactDateOnly === true || settings.ContactDateOnly === 'true'

    return { columns, hiddenKeys, extraTitles, contactDateOnly }
}

export { HOUSEHOLD_COLUMN_CATALOG, CHOOSABLE_COLUMN_KEYS, COLUMN_OWNED_TITLES, columnHidden }
export default resolveHouseholdColumns
